/** Direction of a measured network transfer. */
export type TransferDirection = "download" | "upload";

/** How the app bundle is being served (affects measurement interpretation). */
export type HostDeliveryMode = "vite-dev" | "static-hosting";

/** A single observed HTTP or resource transfer. */
export type NetworkTransfer = {
  id: string;
  url: string;
  method: string;
  direction: TransferDirection;
  bytes: number;
  startTimeMs: number;
  endTimeMs: number;
  durationMs: number;
  /** Instantaneous throughput for this transfer (bytes per second). */
  throughputBps: number;
  source: "fetch" | "xhr" | "resource";
  category: TransferCategory;
};

/** Grouping for UI filters and export summaries. */
export type TransferCategory = "api" | "asset" | "other";

/** One time-bucket in the throughput timeline (default 100 ms). */
export type ThroughputBucket = {
  startTimeMs: number;
  downloadBytes: number;
  uploadBytes: number;
  downloadBps: number;
  uploadBps: number;
};

/** Aggregated bandwidth metrics for a monitoring session. */
export type BandwidthReport = {
  sessionStartMs: number;
  sessionDurationMs: number;
  totalBytesDownloaded: number;
  totalBytesUploaded: number;
  transferCount: number;
  hostDeliveryMode: HostDeliveryMode;
  buildMode: string;

  peakDownloadMbps: number;
  peakUploadMbps: number;
  peakDownloadAtMs: number | null;
  peakUploadAtMs: number | null;
  minSustainedDownloadMbps: number;
  minSustainedUploadMbps: number;
  idleDownloadKbps: number;
  averageDownloadMbps: number;
  averageUploadMbps: number;

  apiBytesDownloaded: number;
  apiBytesUploaded: number;
  assetBytesDownloaded: number;

  largestDownload: NetworkTransfer | null;
  largestUpload: NetworkTransfer | null;

  connectionDownlinkMbps: number | null;
  connectionRttMs: number | null;
  connectionEffectiveType: string | null;

  recentTransfers: NetworkTransfer[];
  throughputTimeline: ThroughputBucket[];
};

export type BandwidthMonitorConfig = {
  bucketWidthMs: number;
  rollingWindowMs: number;
  idleThresholdBytes: number;
  maxTransfers: number;
  verboseLogging: boolean;
  /** Skip response.clone() above this size when Content-Length is absent. */
  maxBodyMeasureBytes: number;
};

export const DEFAULT_BANDWIDTH_MONITOR_CONFIG: BandwidthMonitorConfig = {
  bucketWidthMs: 100,
  rollingWindowMs: 1000,
  idleThresholdBytes: 512,
  maxTransfers: 500,
  verboseLogging: false,
  maxBodyMeasureBytes: 256 * 1024,
};

export type BandwidthMonitorGlobal = {
  getReport: () => BandwidthReport;
  reset: () => void;
  setVerboseLogging: (enabled: boolean) => void;
  markScenario: (name: string) => void;
  getScenarioMarks: () => ReadonlyArray<{
    name: string;
    atMs: number;
    report: BandwidthReport;
  }>;
};

declare global {
  // eslint-disable-next-line @typescript-eslint/consistent-type-definitions -- Window augmentation requires interface
  interface Window {
    __benchmarkBandwidth?: BandwidthMonitorGlobal;
  }
}
